import { type CSSProperties } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  useBrandTokens,
  useSidebarState,
  useResizableSidebar,
  SIDEBAR_COLLAPSED_WIDTH,
  TopBar,
  NavIcon,
  ContactRow,
  ResizeGutter,
  ClinicModePills,
  orgName,
  orgLogoUrl,
  orgLogoHorizontalUrl,
  sidebarLogoMaxWidth,
  sidebarLogoMaxHeight,
  sidebarLogoObjectPosition,
  sidebarLogoMargin,
  orgPhone,
  orgEmail,
} from './shared';
import { useVisibleNav } from '../auth/useVisibleNav';
import { usePinnedRoutes, ROUTE_TO_PIN_KEY } from './usePinnedRoutes';

// ─── Queue Sidebar Nav ───

const QUEUE_NAV = [{ label: 'Queue', icon: 'list-ordered', path: '/clinic/queue' }] as const;

// ─── Pin Icon ───

function PinIcon({ pinned }: { pinned: boolean }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill={pinned ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ transform: 'rotate(45deg)' }}
    >
      <line x1="12" y1="17" x2="12" y2="22" />
      <path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24Z" />
    </svg>
  );
}

// ─── Queue Sidebar ───

function QueueSidebar({
  isPinned,
  togglePinned,
}: {
  isPinned: (key: string) => boolean;
  togglePinned: (key: string) => void;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const visibleQueue = useVisibleNav(QUEUE_NAV);

  return (
    <aside
      className="shell-sidebar pc-sidebar"
      style={{
        width: '100%',
        height: '100%',
        overflowX: 'clip',
        overflowY: 'auto',
        borderRadius: 'var(--radius-xl) 0 0 var(--radius-xl)',
        flexShrink: 0,
      }}
    >
      <div style={{ width: '100%', height: '100%' } as React.CSSProperties}>
        {/* Clinic Info Header */}
        <div
          style={{
            padding: 'var(--sp-6)',
            borderBottom: '1px solid var(--border)',
          }}
        >
          {orgLogoHorizontalUrl ? (
            <img
              src={orgLogoHorizontalUrl}
              alt={orgName}
              style={{
                width: '100%',
                maxWidth: sidebarLogoMaxWidth,
                maxHeight: sidebarLogoMaxHeight,
                display: 'block',
                margin: sidebarLogoMargin,
                objectFit: 'contain',
                objectPosition: sidebarLogoObjectPosition,
              }}
            />
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)' }}>
              {orgLogoUrl ? (
                <img
                  src={orgLogoUrl}
                  alt={orgName}
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 'var(--radius-full)',
                    objectFit: 'cover',
                    flexShrink: 0,
                  }}
                />
              ) : (
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 'var(--radius-full)',
                    background: 'var(--bg-surface-2)',
                    border: '1px solid var(--border)',
                    flexShrink: 0,
                  }}
                />
              )}
              <span
                style={{
                  fontSize: 'var(--font-size-lg)',
                  fontWeight: 'var(--font-weight-semi)',
                  lineHeight: 'var(--line-height-lg)',
                  letterSpacing: '-0.44px',
                  color: 'var(--text)',
                }}
              >
                {orgName}
              </span>
            </div>
          )}

          {/* Contact rows */}
          <div
            style={{
              marginTop: 'var(--sp-4)',
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--sp-3)',
            }}
          >
            <ContactRow
              icon="map-pin"
              text="123 Medical Plaza, Suite 400&#10;San Francisco, CA 94102"
            />
            {orgPhone && <ContactRow icon="phone" text={orgPhone} />}
            {orgEmail && <ContactRow icon="mail" text={orgEmail} />}
          </div>
        </div>

        <ClinicModePills />

        {/* QUEUE Section */}
        {visibleQueue.length > 0 && (
          <div style={{ padding: 'var(--sp-6) var(--sp-6) 0' }}>
            <span
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-semi)',
                lineHeight: 'var(--line-height-sm)',
                letterSpacing: '0.6px',
                textTransform: 'uppercase',
                color: 'var(--text-muted)',
              }}
            >
              Queue
            </span>

            <nav
              style={{
                marginTop: 'var(--sp-4)',
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--sp-1)',
              }}
            >
              {visibleQueue.map((item) => {
                const active = location.pathname === item.path;
                const pinKey = ROUTE_TO_PIN_KEY.get(item.path);
                const pinned = pinKey ? isPinned(pinKey) : false;
                return (
                  <div
                    key={item.label}
                    className="nav-pin-row"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      position: 'relative',
                    }}
                  >
                    <a
                      href={item.path}
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(item.path);
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--sp-3)',
                        height: active ? 48 : 44,
                        padding: active ? '0 18px' : '0 16px',
                        borderRadius: 'var(--radius-md)',
                        background: active ? 'var(--gradient-nav-active)' : 'transparent',
                        border: active
                          ? '2px solid var(--color-primary-contrast)'
                          : '2px solid transparent',
                        color: active ? 'var(--text)' : 'var(--text-muted)',
                        textDecoration: 'none',
                        fontSize: 'var(--font-size-base)',
                        fontWeight: 'var(--font-weight-medium)',
                        lineHeight: 'var(--line-height-base)',
                        letterSpacing: '-0.15px',
                        cursor: 'pointer',
                        flex: 1,
                      }}
                    >
                      <NavIcon name={item.icon} />
                      {item.label}
                    </a>
                    {pinKey && (
                      <button
                        onClick={() => togglePinned(pinKey)}
                        className="pin-btn"
                        style={{
                          position: 'absolute',
                          right: 8,
                          width: 28,
                          height: 28,
                          borderRadius: 'var(--radius-sm)',
                          background: 'transparent',
                          border: 'none',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: pinned ? 'var(--color-primary)' : 'var(--text-muted)',
                          cursor: 'pointer',
                          opacity: pinned ? 1 : 0,
                          transition: 'opacity 150ms ease',
                          padding: 0,
                        }}
                      >
                        <PinIcon pinned={pinned} />
                      </button>
                    )}
                  </div>
                );
              })}
            </nav>
          </div>
        )}
      </div>
    </aside>
  );
}

// ─── Queue Sidebar Rail (collapsed) ───
//
// Mirrors `InventorySidebarRail`: when the sidebar is collapsed we don't
// just clip the full panel (which leaks rotated nav labels and an
// orphan contact column into the rail) — we render a dedicated narrow
// rail that's vertical-stack of icons. Clicking anywhere on the rail
// expands it; clicking a nav icon navigates without expanding.

function QueueSidebarRail({ onExpand }: { onExpand: () => void }) {
  const location = useLocation();
  const navigate = useNavigate();
  const visibleQueue = useVisibleNav(QUEUE_NAV);

  return (
    <aside
      className="shell-sidebar pc-sidebar"
      onClick={onExpand}
      title="Expand sidebar"
      style={{
        width: SIDEBAR_COLLAPSED_WIDTH,
        height: '100%',
        borderRadius: 'var(--radius-xl) 0 0 var(--radius-xl)',
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        paddingTop: 'var(--sp-4)',
        gap: 'var(--sp-1)',
        cursor: 'pointer',
      }}
    >
      {/* Mini logo */}
      {orgLogoUrl ? (
        <img
          src={orgLogoUrl}
          alt={orgName}
          style={{
            width: 32,
            height: 32,
            borderRadius: 'var(--radius-full)',
            objectFit: 'cover',
            flexShrink: 0,
            marginBottom: 'var(--sp-3)',
          }}
        />
      ) : (
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: 'var(--radius-full)',
            background: 'var(--bg-surface-2)',
            border: '1px solid var(--border)',
            flexShrink: 0,
            marginBottom: 'var(--sp-3)',
          }}
        />
      )}

      {visibleQueue.map((item) => {
        const active = location.pathname === item.path;
        return (
          <button
            key={item.path}
            title={item.label}
            onClick={(e) => {
              e.stopPropagation();
              navigate(item.path);
            }}
            style={{
              width: 36,
              height: 36,
              borderRadius: 'var(--radius-md)',
              border: 'none',
              background: active ? 'var(--gradient-nav-active)' : 'transparent',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: active ? 'var(--text)' : 'var(--text-muted)',
              padding: 0,
            }}
          >
            <NavIcon name={item.icon} />
          </button>
        );
      })}
    </aside>
  );
}

// ─── Main Layout ───

export function QueueShellLayout() {
  // Default to collapsed on first visit — the queue sidebar only has the
  // single "Queue" nav item, so the extra column mostly steals space from
  // the queue list. Users can still expand it manually and that choice
  // persists across visits.
  const { collapsed, toggle } = useSidebarState('powercareai:queue-sidebar-collapsed', true);
  const {
    width: sbWidth,
    dragging,
    startDrag,
    resetWidth,
  } = useResizableSidebar({
    storageKey: 'powercareai:queue-sidebar-width',
    min: 280,
    max: 520,
    defaultWidth: 320,
    collapsed,
    onExpand: toggle,
  });
  const brandTokens = useBrandTokens();
  const { isPinned, togglePinned } = usePinnedRoutes();

  return (
    <div
      className="pc-desk"
      style={
        {
          ...brandTokens,
          display: 'grid',
          gridTemplateColumns: '1fr',
          gridTemplateRows: 'var(--topbar-height) 1fr',
          height: '100vh',

          fontFamily: 'var(--font-family)',
        } as CSSProperties
      }
    >
      <TopBar sidebarWidth={collapsed ? SIDEBAR_COLLAPSED_WIDTH : sbWidth} />

      {/* Main content area */}
      <div
        style={{
          padding: 'var(--content-inset)',
          position: 'relative',
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        {/* Main Card */}
        <div
          style={{
            background: 'transparent',
            borderRadius: 'var(--main-card-radius)',
            boxShadow: 'var(--shadow-paper)',
            height: '100%',
            display: 'flex',
            overflow: 'clip',
          }}
        >
          <div
            style={{
              width: collapsed ? SIDEBAR_COLLAPSED_WIDTH : sbWidth,
              flexShrink: 0,
              transition: dragging ? 'none' : 'width 200ms ease-out',
              overflow: 'hidden',
            }}
          >
            {collapsed ? (
              <QueueSidebarRail onExpand={toggle} />
            ) : (
              <QueueSidebar isPinned={isPinned} togglePinned={togglePinned} />
            )}
          </div>

          <ResizeGutter onDragStart={startDrag} onDoubleClick={resetWidth} />

          {/* Content Area — paper */}
          <div
            style={{
              flex: 1,
              minWidth: 0,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
              overflow: 'hidden',
              background: '#ffffff',
              borderRadius: '0 var(--main-card-radius) var(--main-card-radius) 0',
            }}
          >
            <Outlet context={{ collapsed, onToggleSidebar: toggle }} />
          </div>
        </div>
      </div>
    </div>
  );
}
